import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed } from '@angular/core';
import { INIT_MODES, IReportTabState, InitMode } from '@navstation-portal/reporting/data-model/vessel-report';
import {
  CargoDetailsState,
  CargoFormState,
  CargoState,
  HoseHeatingOperationFormState,
  HoseOperationFormState,
  HoseType,
  MAX_CARGOES,
} from '@navstation-portal/reporting/data-model/vessel-report';
import { SelectOption } from '@navstation-portal/shared/data-model/common';
import { signalStore, withComputed, withState } from '@ngrx/signals';
import { initStateField } from '../../utils/store.utility';
import { withFormStoreReducer } from '../with-form-store-reducer';
import { cargoDetailsFormEvents } from './cargo-details-events';
import { buildCargoDetailsVm } from './cargo-details-vm-builder';
import { withCargoDetailsEffects } from './with-cargo-details-effects';
import { withCargoDetailsReducer } from './with-cargo-details-reducer';

const initCargoDetailsStateReducer = (initialData: IReportTabState, initMode: InitMode) => {
  const data = initialData as CargoDetailsState;
  switch (initMode) {
    case INIT_MODES.CARRY_OVER:
      return initWithCarryOver();
    case INIT_MODES.IMPORT:
      return initWithExistingReport(data);
    case INIT_MODES.BLANK:
      return initBlank();
    default:
      return initBlank();
  }
};

// The template carries numberOfCargoes and the cargoGrade column, as V1 does,
// so the init keeps their keys and fills no value. V1 carries no other cargo
// column, so the init fills none. See the form-rule language doc.
const initWithCarryOver = () => {
  return {
    formState: {
      terminal: {
        name: initStateField<undefined>(),
      },
      stowageDetails: {
        numberOfCargoes: initStateField<number>(),
        cargoes: generateCargoArray(),
      },
      otherDetails: {
        hoseDiameter: initStateField<undefined>(),
        protestsReceived: initStateField<undefined>(),
        protestsIssued: initStateField<undefined>(),
        seaTemperature: initStateField<undefined>(),
        airTemperature: initStateField<undefined>(),
        protestsReceivedComments: initStateField<undefined>(),
        protestsIssuedComments: initStateField<undefined>(),
      },
      hoses: {
        operationType: initStateField<undefined>(),
        terminal: initStateField<undefined>(),
        description: initStateField<undefined>(),
        hosesOn: [] as HoseOperationFormState[],
        hosesOff: [] as HoseOperationFormState[],
        heating: [] as HoseHeatingOperationFormState[],
      },
    },
  };
};

const initWithExistingReport = (data: CargoDetailsState) => {
  return {
    formState: {
      terminal: {
        name: initStateField<string>(data.terminal.name),
      },
      stowageDetails: {
        numberOfCargoes: initStateField<number>(data.stowageDetails.numberOfCargoes),
        cargoes: generateCargoArray(data.stowageDetails.cargoes),
      },
      otherDetails: {
        hoseDiameter: initStateField<number>(data.otherDetails.hoseDiameter),
        protestsReceived: initStateField<SelectOption>(data.otherDetails.protestsReceived),
        protestsIssued: initStateField<SelectOption>(data.otherDetails.protestsIssued),
        seaTemperature: initStateField<number>(data.otherDetails.seaTemperature),
        airTemperature: initStateField<number>(data.otherDetails.airTemperature),
        protestsReceivedComments: initStateField<string>(data.otherDetails.protestsReceivedComments),
        protestsIssuedComments: initStateField<string>(data.otherDetails.protestsIssuedComments),
      },
      hoses: {
        operationType: initStateField<SelectOption>(data.hoses.operationType),
        terminal: initStateField<string>(data.hoses.terminal),
        description: initStateField<SelectOption>(data.hoses.description),
        hosesOn: generateHoses(data, HoseType.On) as HoseOperationFormState[],
        hosesOff: generateHoses(data, HoseType.Off) as HoseOperationFormState[],
        heating: generateHoses(data, HoseType.Heating) as HoseHeatingOperationFormState[],
      },
    },
  };
};

const generateHoses = (data: CargoDetailsState, type: HoseType) => {
  if (type === HoseType.Heating) {
    return (data.hoses.heating ?? []).map((hose, index) => ({
      id: initStateField<string>(`hose-heating-${index}`),
      comment: initStateField<string>(hose.comment),
      ifo: initStateField<number>(hose.ifo),
      mgo: initStateField<number>(hose.mgo),
    }));
  }

  const hoseData = type === HoseType.On ? data.hoses.hosesOn : data.hoses.hosesOff;
  return (hoseData ?? []).map((hose, index) => ({
    id: initStateField<string>(`hose-${type}-${index}`),
    operationTimestamp: initStateField<Date>(hose.operationTimestamp),
    ifo: initStateField<number>(hose.ifo),
    lsf: initStateField<number>(hose.lsf),
    lsg: initStateField<number>(hose.lsg),
    mgoOn: initStateField<number>(hose.mgoOn),
  }));
};

const generateCargoObject = (existingCargo?: CargoState) => {
  const cargo: CargoFormState = {
    blCode: initStateField<string>(existingCargo?.blCode ?? ''),
    blDischarge: initStateField<Date>(existingCargo?.blDischarge),
    blIssue: initStateField<Date>(existingCargo?.blIssue),
    blQuantity: initStateField<number>(existingCargo?.blQuantity),
    cargoGrade: initStateField<SelectOption>(existingCargo?.cargoGrade),
    charterer: initStateField<string>(existingCargo?.charterer),
    consignee: initStateField<string>(existingCargo?.consignee),
    density: initStateField<number>(existingCargo?.density),
    destinationPort: initStateField<string>(existingCargo?.destinationPort),
    flashPoint: initStateField<number>(existingCargo?.flashPoint),
    loadRate: initStateField<number>(existingCargo?.loadRate),
    loadTemp: initStateField<number>(existingCargo?.loadTemp),
    pourPoint: initStateField<number>(existingCargo?.pourPoint),
    s2sShipName: initStateField<string>(existingCargo?.s2sShipName),
    shipper: initStateField<string>(existingCargo?.shipper),
    surveyorsCargoQuantity: initStateField<number>(existingCargo?.surveyorsCargoQuantity),
  };

  return cargo;
};

const generateCargoArray = (data?: CargoState[]) => {
  const cargoArray: CargoFormState[] = [];
  for (let i = 0; i < MAX_CARGOES; i++) {
    let existingCargo;
    if (data && i <= data.length) {
      existingCargo = data[i];
    }
    cargoArray.push(generateCargoObject(existingCargo));
  }
  return cargoArray;
};

const initBlank = () => {
  return {
    formState: {
      terminal: {
        name: initStateField<string | undefined>(),
      },
      stowageDetails: {
        numberOfCargoes: initStateField<number>(),
        cargoes: generateCargoArray(),
      },
      otherDetails: {
        hoseDiameter: initStateField<number>(),
        protestsReceived: initStateField<SelectOption>(),
        protestsIssued: initStateField<SelectOption>(),
        seaTemperature: initStateField<number>(),
        airTemperature: initStateField<number>(),
        protestsReceivedComments: initStateField<string>(),
        protestsIssuedComments: initStateField<string>(),
      },
      hoses: {
        operationType: initStateField<SelectOption>(),
        terminal: initStateField<string>(),
        description: initStateField<SelectOption>(),
        hosesOn: [] as HoseOperationFormState[],
        hosesOff: [] as HoseOperationFormState[],
        heating: [] as HoseHeatingOperationFormState[],
      },
    },
  };
};

export const CargoDetailsStore = signalStore(
  { providedIn: 'root' },
  withDevtools('CargoDetailsStore'),
  withState(initBlank()),
  withCargoDetailsEffects(),
  withFormStoreReducer(cargoDetailsFormEvents, initCargoDetailsStateReducer, 'cargoDetails'),
  withCargoDetailsReducer(),
  withComputed((store) => ({
    vm: computed(() => buildCargoDetailsVm(store.formState())),
  }))
);
